/**
 * Backup Command Center (Spec 201 — US5 / FR-024..028)
 *
 * One view answering "is everything protected, and can we restore?" — KPI tiles,
 * a backup-activity trend, protection-coverage + restore-readiness donuts, a
 * derived best-practices panel, and filterable coverage-gap / readiness tables.
 * Built from @/components/ui + recharts per the design blueprint.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DashboardPanel,
  StatCard,
  DataTable,
  Badge,
  Button,
  Input,
  Select,
  LoadingState,
  ErrorState,
  EmptyState,
  AppTooltip,
} from '@/components/ui';
import { PauseCircle, Activity, ShieldCheck, AlertTriangle, CheckCircle2, Info, Database, Clock, HardDrive, History, Layers, Boxes, Network, FileStack, Target, TimerReset, Gauge, Warehouse } from 'lucide-react';
import { backupDashboardApi, CoverageGap, Overview, ReadinessRow } from '../../api/dashboard';
import { recoverabilityApi } from '../../api/recoverability';
import { ListFilterBar, textMatch } from '../common/ListFilterBar';
import { BackupActivityTrend, DonutStat, ItemTypeBar, StorageGrowthArea, fmtBytes, COLOR } from './CommandCenterCharts';
import { ProtectionTierBadge } from '../common/ProtectionTierBadge';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';
import { UnmeasuredSizeTag, unmeasuredSizeNote } from '@/utils/unmeasuredSize';

/** A section header to group the dashboard's many panels (Protection / Storage / Activity / …). */
const Section: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; right?: React.ReactNode }> = ({ icon, title, subtitle, right }) => (
  <div className="mt-2 flex items-end justify-between gap-3 border-b border-[var(--color-border)] pb-2">
    <div className="flex items-center gap-2">
      <span className="text-[var(--color-accent)]">{icon}</span>
      <div>
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{title}</h2>
        {subtitle && <p className="text-xs text-[var(--color-text-secondary)]">{subtitle}</p>}
      </div>
    </div>
    {right}
  </div>
);

const TREND_DAYS = 14;
const rateColor = (r: number) =>
  r >= 95 ? 'text-emerald-600 dark:text-emerald-400' : r >= 80 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/**
 * Spec 298 FR-004a (screen contract § 2): what the success-rate tile adds when no run finished. A schedule that was due, or
 * one whose cron cannot be read, turns "no runs" into a warning; with neither, "no runs" is neutral and this is null.
 */
const noRunsWarning = (ov: Overview): string | null => {
  const unread = ov.unparsedSchedules.length;
  if (ov.dueSchedules > 0) {
    const due = `${count(ov.dueSchedules, 'schedule was', 'schedules were')} due`;
    return unread > 0 ? `${due}, ${unread} cannot be read` : due;
  }
  return unread > 0 ? `${count(unread, 'schedule cannot', 'schedules cannot')} be read` : null;
};

const fmtGb = (gb: number | null): string =>
  gb == null ? '—' : gb >= 1024 ? `${(gb / 1024).toFixed(1)} TB` : gb >= 10 ? `${gb.toFixed(0)} GB` : `${gb.toFixed(1)} GB`;
const fmtDate = (iso: string | null): string =>
  iso ? formatEasternDate(iso) : '—';
const fmtDur = (h: number | null): string => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h >= 168 && h % 168 === 0) return `${h / 168}wk`;
  if (h >= 24 && h % 24 === 0) return `${h / 24}d`;
  return `${h}h`;
};
const fmtRto = (sec: number | null): string => {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};
const pctColor = (p: number) =>
  p >= 95 ? 'text-emerald-600 dark:text-emerald-400' : p >= 80 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';

// The three protected asset classes — the dashboard treats all of them as first-class.
const CLASS_META = {
  lakehouse: { label: 'Lakehouses', icon: Database },
  warehouse: { label: 'Warehouses', icon: Warehouse },
  workspace: { label: 'Workspaces (DR)', icon: Network },
} as const;

export const CommandCenter: React.FC = () => {
  const overview = useQuery({ queryKey: ['backup', 'dash', 'overview'], queryFn: backupDashboardApi.overview });
  const coverage = useQuery({ queryKey: ['backup', 'dash', 'coverage'], queryFn: backupDashboardApi.coverage });
  const readiness = useQuery({ queryKey: ['backup', 'dash', 'readiness'], queryFn: backupDashboardApi.readiness });
  const trend = useQuery({ queryKey: ['backup', 'dash', 'trend', TREND_DAYS], queryFn: () => backupDashboardApi.trend(TREND_DAYS) });
  const recov = useQuery({ queryKey: ['backup', 'dash', 'recoverability'], queryFn: recoverabilityApi.summary });
  const insightsQ = useQuery({ queryKey: ['backup', 'dash', 'insights'], queryFn: backupDashboardApi.insights });
  const ins = insightsQ.data;

  const readinessRows = readiness.data ?? [];
  // Spec 292 D4 (owner, 2026-10-28): at-risk and within-RPO are judged over targets of ACTIVE plans only. A paused
  // plan is not protecting, so "overdue" says nothing about its targets: they are counted on their own tile
  // (pausedCount). Targets of draft/archived plans are in neither figure (analyze U1: outside readiness).
  const activeRows = readinessRows.filter((r) => r.planStatus === 'active');
  const atRisk = activeRows.filter((r) => r.atRisk);
  const onTrack = activeRows.length - atRisk.length;
  const pausedCount = readinessRows.filter((r) => r.planStatus === 'paused').length;
  // RPO compliance = active-plan targets whose last backup is within their RPO window (the same
  // tolerance-aware at-risk logic the readiness table uses). RTO compliance comes from insights.
  // Spec 298 FR-004: null over no active-plan target; the panel says so instead of a 0% donut.
  const rpoPct = activeRows.length > 0 ? Math.round((onTrack / activeRows.length) * 1000) / 10 : null;
  // Comprehensive protection across ALL classes (lakehouse + warehouse + workspace), not LH/WH only.
  const pbc = ins?.protectionByClass ?? [];
  const totalPlanned = pbc.reduce((s, c) => s + c.withPlan, 0);
  const totalWithPoints = pbc.reduce((s, c) => s + c.withPoints, 0);
  // 🛑 The completion ring MUST divide like by like. `withPoints` is a D1 count (any protectable
  // entity holding a point); `totalPlanned` is D2 (ACTIVE plan only). Dividing one by the other read
  // **1600%** on a paused estate -- 16 entities keep points from before the pause while 1 still has
  // an active plan. `withPlanAndPoints` is the intersection, computed in the same SQL that computes
  // the denominator so the subset relation holds by construction rather than by clamping.
  const totalCoveredWithPoints = pbc.reduce((s, c) => s + (c.withPlanAndPoints ?? 0), 0);
  const totalEntitiesAllClasses = pbc.reduce((s, c) => s + c.entities, 0);
  // 🛑 This total spans ALL THREE classes (lakehouse D1 + warehouse D1 + DR-ENROLLED workspaces),
  // which is NOT the same population as the "Protection coverage gaps" table below — that one is
  // lakehouse+warehouse only. On dev the two read 322 and 134. An earlier draft of the caption
  // cross-referenced that table, which would have sent a reader to look for 321 rows in a list of
  // 133. The caption now names its own composition instead of pointing at a different denominator:
  // two honest numbers on one screen still mislead if the reader is invited to equate them.
  // Spec 211 — total entities managed out of coverage across all classes (backend-provided).
  const totalExcluded = pbc.reduce((s, c) => s + (c.excluded ?? 0), 0);
  const completionPct = totalPlanned > 0 ? Math.round((totalCoveredWithPoints / totalPlanned) * 100) : null;
  const byClass = (cls: 'lakehouse' | 'warehouse' | 'workspace') => pbc.find((c) => c.cls === cls);

  // ── Filters (Part 1) ───────────────────────────────────────────────────────
  const [gapQ, setGapQ] = useState('');
  const [gapType, setGapType] = useState('');
  const [rdQ, setRdQ] = useState('');
  const [rdStatus, setRdStatus] = useState('');

  const gaps = coverage.data?.gaps ?? [];
  const filteredGaps = useMemo(
    () => gaps.filter((g) => (!gapType || g.entityType === gapType) && textMatch(gapQ, g.name, g.entityId, g.entityType)),
    [gaps, gapQ, gapType]
  );
  const filteredReadiness = useMemo(
    () =>
      readinessRows.filter(
        (r) =>
          (!rdStatus || (rdStatus === 'atrisk' ? r.atRisk : !r.atRisk)) &&
          textMatch(rdQ, r.name, r.workspaceName, r.planName, r.entityId, r.entityType)
      ),
    [readinessRows, rdQ, rdStatus]
  );

  // ── Derived best-practice insights (meaningful, actionable) ─────────────────
  const insights = useMemo(() => {
    const out: { tone: 'ok' | 'warn' | 'info'; text: string }[] = [];
    if (coverage.data) {
      out.push(
        gaps.length === 0
          ? { tone: 'ok', text: `All ${coverage.data.total} backup-capable entities are actively protected by an active plan.` }
          : { tone: 'warn', text: `${gaps.length} of ${coverage.data.total} entities aren't actively protected — ${gaps.filter((g) => g.reason === 'Plan paused').length} have a paused plan, ${gaps.filter((g) => g.reason === 'No plan').length} have no plan.` }
      );
    }
    out.push(
      atRisk.length === 0
        ? { tone: 'ok', text: 'Every planned target is within its RPO window.' }
        : { tone: 'warn', text: `${atRisk.length} target(s) exceed their RPO — review schedules and last backups.` }
    );
    if (overview.data) {
      // Spec 214 D3: strict rate (Partial ≠ success); null = nothing finished — say so, never 100%/0%.
      const sr = overview.data.successRate;
      out.push(
        sr == null
          ? { tone: 'info', text: 'No backup runs finished in the last 30 days — no success rate to report yet.' }
          : sr >= 95
            ? { tone: 'ok', text: `Backup success rate is ${sr}% (strict — Partial counts as not-success; target ≥ 95%).` }
            : { tone: 'warn', text: `Success rate is ${sr}% — below the 95% target; investigate failures${overview.data.partial > 0 ? ` (${overview.data.partial} partial)` : ''}.` }
      );
    }
    if (recov.data) {
      const { total, recoverable, atRisk: rAtRisk, verifiedPct } = recov.data;
      out.push(
        total === 0
          ? { tone: 'info', text: 'No assets to verify yet.' }
          : recoverable === total
            ? { tone: 'ok', text: `All ${total} assets are proven recoverable (verified within SLA).` }
            : verifiedPct === 0
              ? { tone: 'info', text: `Recoverability not yet proven — all ${rAtRisk} asset(s) are unverified (expected at first). Turn on automated verification on the Recoverability tab to prove restores actually work.` }
              : { tone: 'warn', text: `${rAtRisk} of ${total} asset(s) are not proven recoverable (unverified, last failed, or out of SLA).` }
      );
    }
    if (ins && ins.compliance.rtoMeasured > 0) {
      out.push(
        ins.compliance.rtoMet >= ins.compliance.rtoMeasured
          ? { tone: 'ok', text: `All ${ins.compliance.rtoMet} verified restore(s) completed within their RTO target (avg ${fmtRto(ins.compliance.avgObservedRtoSeconds)}).` }
          : { tone: 'warn', text: `${ins.compliance.rtoMeasured - ins.compliance.rtoMet} of ${ins.compliance.rtoMeasured} verified restore(s) exceeded their RTO target.` }
      );
    }
    if (ins && ins.workspaceDr.pointerItems > 0) {
      out.push({ tone: 'info', text: `Workspace DR dedup is saving ${ins.workspaceDr.dedupPct}% — ${ins.workspaceDr.pointerItems} unchanged item(s) reused instead of re-stored.` });
    }
    out.push({ tone: 'info', text: 'Follow 3-2-1: keep 3 copies on 2 media with 1 off-platform (blob archive).' });
    return out;
  }, [coverage.data, overview.data, gaps.length, atRisk.length, recov.data, ins]);

  if (overview.isLoading && coverage.isLoading && readiness.isLoading) return <LoadingState rows={6} />;

  const ov = overview.data;
  const cov = coverage.data;
  const noRuns = ov && ov.successRate == null ? noRunsWarning(ov) : null;

  return (
    <div className="space-y-5">
      {/* ═══ Protection at a glance ═════════════════════════════════════════ */}
      <Section
        icon={<ShieldCheck className="h-5 w-5" />}
        title="Protection at a glance"
        subtitle="Backup success and protection coverage across lakehouses, warehouses AND workspace-complete DR (last 30 days)"
      />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-7">
        <StatCard
          label="Success rate (30d)"
          value={ov ? (ov.successRate == null ? 'no runs' : `${ov.successRate}%`) : '—'}
          valueClassName={ov && ov.successRate != null ? rateColor(ov.successRate) : noRuns ? 'text-[var(--color-status-warning)]' : undefined}
          subtext={ov && ov.successRate != null ? `strict · ${ov.partial > 0 ? `${ov.partial} partial · ` : ''}of ${ov.finished} finished` : noRuns ?? 'all backup types'}
          icon={<ShieldCheck className="h-4 w-4" />}
        />
        <StatCard
          label="Backups run (30d)"
          value={ov ? String(ov.total) : '—'}
          subtext={ov ? `${ov.byType.lakehouse.total} LH · ${ov.byType.warehouse.total} WH · ${ov.byType.workspace.total} WS` : undefined}
          icon={<Activity className="h-4 w-4" />}
        />
        <StatCard
          label="Protected entities"
          value={ins ? String(totalPlanned) : '—'}
          subtext={
            ins
              ? `${byClass('lakehouse')?.withPlan ?? 0} LH · ${byClass('warehouse')?.withPlan ?? 0} WH · ${byClass('workspace')?.withPlan ?? 0} WS${totalExcluded > 0 ? ` · ${totalExcluded} excluded` : ''}`
              : undefined
          }
          icon={<Database className="h-4 w-4" />}
        />
        <StatCard
          label="At-risk (LH/WH RPO)"
          value={String(atRisk.length)}
          valueClassName={atRisk.length > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}
          subtext="active-plan targets overdue"
          icon={<AlertTriangle className="h-4 w-4" />}
        />
        <StatCard
          label="Paused (LH/WH)"
          value={readiness.data ? String(pausedCount) : '—'}
          subtext="targets of paused plans"
          icon={<PauseCircle className="h-4 w-4" />}
        />
        <StatCard
          label="Within RPO (LH/WH)"
          value={readiness.data ? String(onTrack) : '—'}
          subtext={readiness.data ? `of ${activeRows.length} active-plan targets` : undefined}
          icon={<Clock className="h-4 w-4" />}
        />
        <StatCard
          label="Verified recoverable"
          value={recov.data ? (recov.data.verifiedPct === null ? 'nothing to verify' : `${recov.data.verifiedPct}%`) : '—'}
          valueClassName={
            recov.data && recov.data.verifiedPct !== null
              ? recov.data.verifiedPct >= 80
                ? 'text-emerald-600 dark:text-emerald-400'
                : recov.data.verifiedPct > 0
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-[var(--color-text-secondary)]'
              : undefined
          }
          // Spec 246 US5: split degraded from fully-recoverable + state the countable ceiling so the
          // tile never implies byte-perfect verification.
          subtext={
            recov.data
              ? `${recov.data.recoverable} proven${recov.data.byVerdict?.degraded ? ` · ${recov.data.byVerdict.degraded} degraded` : ''} · ${recov.data.atRisk} to verify — countable, not byte-verified`
              : undefined
          }
          icon={<ShieldCheck className="h-4 w-4" />}
        />
      </div>

      {/* ── Protection by asset class (comprehensive: all three classes) ────── */}
      <DashboardPanel title="Protection by asset class" subtitle="Coverage and recovery points for each protected class — lakehouse, warehouse, and workspace definitions">
        {!ins ? (
          <LoadingState rows={3} />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {(['lakehouse', 'warehouse', 'workspace'] as const).map((cls) => {
              const c = byClass(cls);
              const meta = CLASS_META[cls];
              const Icon = meta.icon;
              // Spec 211 — exclusion-aware coverage: prefer the backend's is_excluded-aware
              // numbers (X of Y, Y already nets out managed-out entities) and fall back to the
              // legacy plan-based numbers before the backend ships them. No client recompute.
              const excluded = c?.excluded ?? 0;
              const denom = c?.total ?? c?.entities ?? 0; // Y
              const numer = c?.protected ?? c?.withPlan ?? 0; // X
              const planPct = denom > 0 ? Math.round((numer / denom) * 100) : null;
              const coverGap = c ? c.withPlan - c.withPoints : 0;
              return (
                <div key={cls} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <Icon className="h-5 w-5 text-[var(--color-accent)]" />
                    <span className="font-medium text-[var(--color-text-primary)]">{meta.label}</span>
                    {excluded > 0 && (
                      <Badge variant="default" title={`${excluded} entit${excluded === 1 ? 'y' : 'ies'} managed out of coverage`}>
                        Excluded ({excluded})
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[var(--color-text-primary)]">{numer}</span>
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      {cls === 'workspace' ? 'enrolled in DR' : `of ${denom} protected`}
                    </span>
                    {cls !== 'workspace' && planPct !== null && <Badge variant={planPct >= 100 ? 'success' : 'warning'}>{planPct}%</Badge>}
                  </div>
                  <div className="mt-3 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--color-text-secondary)]">With recovery points</span>
                      <span className="font-medium text-[var(--color-text-primary)]">
                        {c?.withPoints ?? 0}
                        {/* coverGap = protected (active plan) minus those with a restorable point. It was
                            labelled "pending", which promises "a backup is coming" — false for an entity
                            whose backups complete but capture nothing (e.g. an empty/near-empty table) or
                            one that cannot be backed up at all. State the fact, not a promise, and point
                            to Readiness for the cause. */}
                        {coverGap > 0 && (
                          <span
                            className="ml-1 text-xs text-amber-600 dark:text-amber-400"
                            title="Protected by an active plan but with no restorable recovery point. This is NOT a promise that one is coming — it can be a new plan, a genuinely empty entity, or a failing/unbackupable one. See the Readiness tab for the reason per entity."
                          >
                            ({coverGap} no recovery point)
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--color-text-secondary)]">Recovery points</span>
                      <span className="font-medium text-[var(--color-text-primary)]">{c?.recoveryPoints ?? 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--color-text-secondary)]">Last backup</span>
                      <span className="font-medium text-[var(--color-text-primary)]">{c?.lastBackupAt ? fmtDate(c.lastBackupAt) : 'never'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DashboardPanel>

      {/* ── Activity trend ─────────────────────────────────────────────────── */}
      <DashboardPanel title={`Backup activity (${TREND_DAYS} days)`} subtitle="Succeeded vs failed runs per day — lakehouse, warehouse & workspace (hover for the split by type)">
        {trend.isLoading ? <LoadingState rows={3} /> : trend.isError ? <ErrorState onRetry={() => trend.refetch()} /> : (
          <BackupActivityTrend data={trend.data ?? []} />
        )}
      </DashboardPanel>

      {/* ═══ Recovery objectives — RPO & RTO (lakehouse & warehouse only) ════ */}
      <Section
        icon={<Target className="h-5 w-5" />}
        title="Recovery objectives — RPO & RTO (lakehouse & warehouse)"
        subtitle="Data backups vs their recovery-point (freshness) and recovery-time (restore speed) targets. Workspace DR captures definitions continuously/incrementally and has no RPO/RTO tier."
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <DashboardPanel title="RPO compliance" subtitle="Lakehouse/warehouse targets whose latest data backup is within their RPO window">
          {readiness.data ? (rpoPct === null ? (
            <EmptyState title="No active-plan targets" message="No lakehouse or warehouse is under an active plan, so there is no recovery point to measure." />
          ) : (
            <>
              <DonutStat
                slices={[
                  { name: 'Within RPO', value: onTrack, color: COLOR.onTrack },
                  { name: 'Breached', value: atRisk.length, color: COLOR.atRisk },
                ]}
                centerValue={<span className={pctColor(rpoPct)}>{rpoPct}%</span>}
                centerLabel="within RPO"
              />
              <p className="mt-1 text-center text-xs text-[var(--color-text-secondary)]">
                {onTrack} of {activeRows.length} active-plan targets meet their recovery-point objective.
              </p>
            </>
          )) : (
            <LoadingState rows={3} />
          )}
        </DashboardPanel>

        <DashboardPanel title="RTO compliance" subtitle="Verified test-restores completing within the tier's recovery-time target">
          {!ins ? (
            <LoadingState rows={3} />
          ) : ins.compliance.rtoMeasured === 0 ? (
            <EmptyState
              title="No restore time proven yet"
              message="Run a recovery verification (Recoverability tab) to measure real restore time vs the RTO target."
            />
          ) : (
            <>
              <DonutStat
                slices={[
                  { name: 'Within RTO', value: ins.compliance.rtoMet, color: COLOR.onTrack },
                  { name: 'Over target', value: ins.compliance.rtoMeasured - ins.compliance.rtoMet, color: COLOR.atRisk },
                ]}
                centerValue={ins.compliance.rtoPct === null ? '—' : <span className={pctColor(ins.compliance.rtoPct)}>{ins.compliance.rtoPct}%</span>}
                centerLabel="within RTO"
              />
              <p className="mt-1 text-center text-xs text-[var(--color-text-secondary)]">
                {ins.compliance.rtoMet} of {ins.compliance.rtoMeasured} verified ·{' '}
                <span className="font-medium text-[var(--color-text-primary)]">avg restore {fmtRto(ins.compliance.avgObservedRtoSeconds)}</span>
              </p>
            </>
          )}
        </DashboardPanel>

        <DashboardPanel title="Objectives by tier" subtitle="RPO / RTO target each protection tier promises (lakehouse & warehouse)">
          {!ins ? (
            <LoadingState rows={3} />
          ) : (
            <div className="space-y-2">
              {ins.compliance.tiers.filter((t) => t.assets > 0).length === 0 ? (
                <p className="text-sm text-[var(--color-text-secondary)]">No assets assigned to a protection tier yet.</p>
              ) : (
                ins.compliance.tiers
                  .filter((t) => t.assets > 0)
                  .map((t) => (
                    <div key={t.tier} className="flex items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm">
                      <span className="flex items-center gap-2">
                        <ProtectionTierBadge tier={t.tier} size="sm" />
                        <span className="text-xs text-[var(--color-text-secondary)]">{t.assets} asset{t.assets === 1 ? '' : 's'}</span>
                      </span>
                      <span className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1 text-[var(--color-text-secondary)]"><Gauge className="h-3.5 w-3.5" /> RPO {fmtDur(t.rpoHours)}</span>
                        <span className="flex items-center gap-1 text-[var(--color-text-secondary)]"><TimerReset className="h-3.5 w-3.5" /> RTO {fmtDur(t.rtoHours)}</span>
                      </span>
                    </div>
                  ))
              )}
            </div>
          )}
        </DashboardPanel>
      </div>

      {/* ═══ Storage, retention & workspace DR ══════════════════════════════ */}
      <Section
        icon={<HardDrive className="h-5 w-5" />}
        title="Storage, retention & workspace DR"
        subtitle="Protected size & growth, how far back we can recover, incremental savings, and Spec-205 workspace-definition coverage"
      />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Protected data"
          value={ins ? fmtBytes(ins.storage.totalBytes) : '—'}
          subtext={ins
            ? [`${ins.storage.byAccount.length} destination${ins.storage.byAccount.length === 1 ? '' : 's'}`,
                unmeasuredSizeNote(ins.storage.sizeNotMeasuredCount)].filter(Boolean).join(' · ')
            : undefined}
          icon={<HardDrive className="h-4 w-4" />}
        />
        <StatCard
          label="Recovery points"
          value={ins ? String(ins.recoveryDepth.totalPoints) : '—'}
          subtext={
            ins
              ? `${byClass('lakehouse')?.withPoints ?? 0}/${byClass('lakehouse')?.entities ?? 0} LH · ${byClass('warehouse')?.withPoints ?? 0}/${byClass('warehouse')?.entities ?? 0} WH covered`
              : undefined
          }
          icon={<Layers className="h-4 w-4" />}
        />
        <StatCard
          label="Retention depth"
          value={ins?.recoveryDepth.retentionDays != null ? `${ins.recoveryDepth.retentionDays}d` : '—'}
          subtext={ins?.recoveryDepth.oldestPointAt ? `oldest ${fmtDate(ins.recoveryDepth.oldestPointAt)}` : undefined}
          icon={<History className="h-4 w-4" />}
        />
        <StatCard
          label="Incremental mix"
          value={ins ? (ins.dataReduction.incrementalPct === null ? 'no runs' : `${ins.dataReduction.incrementalPct}%`) : '—'}
          subtext={ins ? `${ins.dataReduction.incrementalRuns} incr · ${ins.dataReduction.fullRuns} full (30d)` : undefined}
          icon={<Boxes className="h-4 w-4" />}
        />
        <StatCard
          label="Workspaces (DR)"
          value={ins ? `${ins.workspaceDr.protectedWorkspaces}/${ins.workspaceDr.enrolledWorkspaces}` : '—'}
          subtext={ins ? 'definitions captured' : undefined}
          icon={<Network className="h-4 w-4" />}
        />
        <StatCard
          label="Dedup savings"
          value={ins ? (ins.workspaceDr.dedupPct === null ? 'nothing captured' : `${ins.workspaceDr.dedupPct}%`) : '—'}
          valueClassName={ins && ins.workspaceDr.dedupPct !== null && ins.workspaceDr.dedupPct >= 50 ? 'text-emerald-600 dark:text-emerald-400' : undefined}
          subtext={ins ? `${ins.workspaceDr.pointerItems} of ${ins.workspaceDr.capturedItems + ins.workspaceDr.pointerItems} items reused` : undefined}
          icon={<FileStack className="h-4 w-4" />}
        />
      </div>

      {/* ── Workspace-Complete DR + Storage growth ─────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DashboardPanel title="Workspace-Complete DR" subtitle="Item definitions captured at each workspace's latest recovery point">
          {!ins ? (
            <LoadingState rows={3} />
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-2">
                  <div className="text-lg font-semibold text-[var(--color-text-primary)]">{ins.workspaceDr.capturedItems}</div>
                  <div className="text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">captured</div>
                </div>
                <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-2">
                  <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{ins.workspaceDr.pointerItems}</div>
                  <div className="text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">deduped</div>
                </div>
                <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-2">
                  <div className="text-lg font-semibold text-amber-600 dark:text-amber-400">{ins.workspaceDr.unsupportedItems}</div>
                  <div className="text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">unsupported</div>
                </div>
              </div>
              <ItemTypeBar data={ins.workspaceDr.itemsByType} />
              <p className="text-xs text-[var(--color-text-secondary)]">
                {ins.workspaceDr.latestCaptureAt ? `Latest capture ${formatEastern(ins.workspaceDr.latestCaptureAt)}.` : 'No definition captures yet.'}{' '}
                Unchanged items are deduplicated (Pointer) — only changed definitions are re-stored.
              </p>
            </div>
          )}
        </DashboardPanel>

        <DashboardPanel title="Protected data & growth" subtitle="Cumulative protected footprint (30d) + 60-day forecast · size by destination">
          {!ins ? (
            <LoadingState rows={3} />
          ) : (
            <div className="space-y-3">
              <StorageGrowthArea data={ins.storage.growth} />
              <div className="space-y-1.5">
                {ins.storage.byAccount.length === 0 ? (
                  <p className="text-xs text-[var(--color-text-secondary)]">No protected data yet.</p>
                ) : (
                  ins.storage.byAccount.map((a) => (
                    <div key={a.account} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-[var(--color-text-secondary)]">
                        <HardDrive className="h-3.5 w-3.5" /> {a.account}
                        <span className="text-xs text-[var(--color-text-secondary)]">· {a.assets} asset{a.assets === 1 ? '' : 's'}</span>
                      </span>
                      <span className="font-medium text-[var(--color-text-primary)]">
                        {fmtBytes(a.bytes)}
                        <UnmeasuredSizeTag count={a.sizeNotMeasuredCount} />
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </DashboardPanel>
      </div>

      {/* ═══ Coverage & restore readiness ═══════════════════════════════════ */}
      <Section
        icon={<Database className="h-5 w-5" />}
        title="Coverage & restore readiness"
        subtitle="Unprotected entities and data targets overdue vs their RPO window"
      />

      {/* ── Coverage donut + best practices ────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DashboardPanel
          title="Backup completion"
          subtitle={`Of the ${totalPlanned} entit${totalPlanned === 1 ? 'y' : 'ies'} covered by an ACTIVE plan, how many hold at least one recovery point. Paused plans are NOT counted as covered.`}
        >
          {ins ? (completionPct === null ? (
            <EmptyState title="No entity is covered by an active plan" message="Assign a plan to a lakehouse, warehouse or workspace and its backups count here." />
          ) : (
            <>
            <DonutStat
              slices={[
                { name: 'Backed up', value: totalCoveredWithPoints, color: COLOR.protected },
                // The Math.max(0, ...) clamp that used to live here was hiding the mismatch in the
                // chart while the % label exposed it. With the intersection the subtraction cannot
                // go negative, so the clamp is no longer load-bearing -- kept only as a floor.
                { name: 'Awaiting first backup', value: Math.max(0, totalPlanned - totalCoveredWithPoints), color: COLOR.unprotected },
              ]}
              centerValue={`${completionPct}%`}
              centerLabel="backed up"
            />
            {/* 🛑 WITHOUT THIS LINE THE FIX TRADES ONE DISHONESTY FOR ANOTHER.
                Correcting the ratio makes it divide by the ACTIVE-plan population -- which on a
                paused estate is tiny. "100%" off a denominator of 1, next to 132 paused plans,
                reads as reassurance. The ring is now accurate about a deliberately narrow
                question, so the page has to say out loud how narrow it is. */}
            {totalPlanned < totalEntitiesAllClasses && (
              <p className="mt-2 text-xs text-[var(--color-text-tertiary)]">
                {totalEntitiesAllClasses - totalPlanned} of {totalEntitiesAllClasses} protectable
                entities — {pbc.map((c) => `${c.entities} ${c.cls}${c.entities === 1 ? '' : 's'}`).join(' + ')} —
                are <strong>not</strong> covered by an active plan and are excluded from this ring.{' '}
                {totalWithPoints} entit{totalWithPoints === 1 ? 'y holds' : 'ies hold'} a recovery
                point overall.
              </p>
            )}
            </>
          )) : (
            <LoadingState rows={3} />
          )}
        </DashboardPanel>

        <DashboardPanel title="Health signals & recommendations" subtitle="What's good, what needs attention, and the 3-2-1 reminder">
          <ul className="space-y-3">
            {insights.map((ins, i) => (
              <li key={i} className="flex items-start gap-2">
                {ins.tone === 'ok' ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                ) : ins.tone === 'warn' ? (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                ) : (
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
                )}
                <span className="text-sm text-[var(--color-text-secondary)]">{ins.text}</span>
              </li>
            ))}
          </ul>
        </DashboardPanel>
      </div>

      {/* ── Coverage gaps (filterable) ─────────────────────────────────────── */}
      <DashboardPanel title="Protection coverage gaps" subtitle="Backup-capable entities not actively protected — no plan, or the plan is paused">
        {coverage.isLoading ? (
          <LoadingState rows={3} />
        ) : coverage.isError ? (
          <ErrorState onRetry={() => coverage.refetch()} />
        ) : !coverage.data ? (
          // Spec 298 FR-006 (zero-fallbacks row 26): a paused read has no data and is not loading; never "100% · All 0"
          <LoadingState rows={3} />
        ) : coverage.data.total === 0 ? (
          <EmptyState title="No backup-capable lakehouses or warehouses yet" message="Coverage is measured once the estate holds a lakehouse or warehouse that can be backed up." />
        ) : gaps.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 dark:text-emerald-400" />
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">100% coverage</p>
            <p className="text-sm text-[var(--color-text-secondary)]">
              All {coverage.data.total} backup-capable lakehouses &amp; warehouses are governed by an active plan — no gaps.
            </p>
          </div>
        ) : (
          <>
            <ListFilterBar query={gapQ} onQuery={setGapQ} placeholder="Search entities…" count={filteredGaps.length} total={gaps.length}>
              <Select
                value={gapType}
                onChange={(e) => setGapType(e.target.value)}
                options={[
                  { value: '', label: 'All types' },
                  { value: 'lakehouse', label: 'Lakehouse' },
                  { value: 'warehouse', label: 'Warehouse' },
                ]}
              />
            </ListFilterBar>
            <DataTable<CoverageGap>
              minWidth="52rem"
              columns={[
                { key: 'entityType', header: 'Type', width: '7rem', render: (g) => <Badge variant="default">{g.entityType}</Badge> },
                { key: 'name', header: 'Name', render: (g) => g.name ?? g.entityId },
                { key: 'workspaceName', header: 'Workspace', width: '13rem', sortable: false, render: (g) => g.workspaceName ?? '—' },
                {
                  key: 'reason', header: 'Reason', width: '9rem', sortable: false,
                  render: (g) => <Badge variant={g.reason === 'Plan paused' ? 'warning' : 'danger'}>{g.reason}</Badge>,
                },
                {
                  key: 'sizeGb', header: 'Size', width: '6rem', sortable: false,
                  // Spec 286 T159 item 32: not sized by the last collection, the stored size is its last known one.
                  render: (g) => (g.sizeMeasurementError ? (
                    <AppTooltip title={`The last collection did not measure this ${g.entityType} (${g.sizeMeasurementError}).${g.sizeGb ? ` Last known size: ${fmtGb(g.sizeGb)}.` : ''}`}>
                      <span className="text-[var(--color-text-secondary)]">Not measured</span>
                    </AppTooltip>
                  ) : fmtGb(g.sizeGb)),
                },
                { key: 'tableCount', header: 'Tables', width: '6rem', sortable: false, render: (g) => (g.tableCount ?? '—') },
              ]}
              rows={filteredGaps}
              rowKey={(g) => `${g.entityType}:${g.entityId}`}
            />
          </>
        )}
      </DashboardPanel>

      {/* ── Backup freshness vs RPO (filterable) ───────────────────────────── */}
      <DashboardPanel title="Backup freshness vs RPO (lakehouse & warehouse)" subtitle="Data targets whose latest successful backup is overdue vs their RPO window. (Proven restore readiness is the 'Verified recoverable' KPI / Recoverability tab.)">
        {readiness.isLoading ? (
          <LoadingState rows={3} />
        ) : readiness.isError ? (
          <ErrorState onRetry={() => readiness.refetch()} />
        ) : readinessRows.length === 0 ? (
          <EmptyState title="No planned targets yet" message="Create a plan to track backup freshness." />
        ) : (
          <>
            <ListFilterBar query={rdQ} onQuery={setRdQ} placeholder="Search targets…" count={filteredReadiness.length} total={readinessRows.length}>
              <Select
                value={rdStatus}
                onChange={(e) => setRdStatus(e.target.value)}
                options={[
                  { value: '', label: 'All statuses' },
                  { value: 'atrisk', label: 'At risk' },
                  { value: 'ontrack', label: 'On track' },
                ]}
              />
            </ListFilterBar>
            <DataTable<ReadinessRow>
              minWidth="50rem"
              columns={[
                { key: 'entityType', header: 'Type', width: '6rem', render: (r) => <Badge variant="default">{r.entityType}</Badge> },
                { key: 'name', header: 'Name', render: (r) => r.name ?? r.entityId },
                { key: 'workspaceName', header: 'Workspace', width: '11rem', wrap: true, sortable: false, render: (r) => r.workspaceName ?? '—' },
                {
                  key: 'protectionTier',
                  header: 'Tier · RPO',
                  width: '8rem',
                  sortable: false,
                  render: (r) => (
                    <span className="flex items-center gap-2">
                      <ProtectionTierBadge tier={r.protectionTier} size="sm" />
                      <span className="text-xs text-[var(--color-text-secondary)]">{fmtDur(r.rpoHours)}</span>
                    </span>
                  ),
                },
                {
                  key: 'lastSuccessfulBackup',
                  header: 'Last backup',
                  width: '11rem',
                  render: (r) => (r.lastSuccessfulBackup ? formatEastern(r.lastSuccessfulBackup) : 'never'),
                },
                {
                  key: 'atRisk',
                  header: 'Status',
                  width: '9rem',
                  sortable: false,
                  render: (r) => (
                    <span className="flex flex-col items-start gap-1">
                      {/* Spec 292 D4: a target of a paused (or draft/archived) plan is not judged against its RPO — the tiles above do not count it either */}
                      {r.planStatus !== 'active' ? <Badge variant="default">{r.planStatus ?? 'no plan'}</Badge> : r.atRisk ? <Badge variant="danger">at risk</Badge> : <Badge variant="success">on track</Badge>}
                      {r.scheduleMeetsRpo === false && (
                        <span className="text-xs text-[var(--color-status-warning)]">schedule too sparse for RPO</span>
                      )}
                    </span>
                  ),
                },
              ]}
              rows={filteredReadiness}
              rowKey={(r) => `${r.entityType}:${r.entityId}`}
            />
          </>
        )}
      </DashboardPanel>
    </div>
  );
};

export default CommandCenter;
